import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from 'react-router-dom';
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { BASE_PATH, colors, IMPORT_PATH, TABS } from './constants';
import { ProfitIqProvider, useProfitIqContext } from './context/ProfitIqContext';
import ProfitIqHeader from './components/ProfitIqHeader';
import ProfitIqLocked from './components/ProfitIqLocked';
import { ScreenLoadingProvider } from './components/ScreenLoading';
import {
  UnsavedChangesProvider,
  useHasUnsavedWork,
  useUnsavedChangesGuard,
} from './components/UnsavedChanges';
import { useBusinessId } from './useBusinessId';
import { useProfitIqAccess } from './useProfitIqAccess';

import Overview from './screens/Overview';
import MenuItems from './screens/MenuItems';
import Categories from './screens/Categories';
import Channels from './screens/Channels';
import MyCosts from './screens/MyCosts';
import ProfitSettings from './screens/ProfitSettings';
import Insights from './screens/Insights';
import ImportSales from './screens/ImportSales';

/**
 * The two reads an edit leaves stale: both rebuild the dashboard, which costs
 * calls out to MMS for the catalog, its categories and the inventory costs.
 */
const DASHBOARD_KEYS = ['profit-analytics', 'profit-menu-items'];

/**
 * Entry point for the Profit IQ section. Owns a React Query client scoped to
 * this section (server state stays out of global Redux), the selection context
 * every tab reads, and the section's nested routes.
 *
 * The mobile app renders these seven views as pill tabs inside one drawer
 * destination. Here they are tabs bound to the URL, so a tab is linkable and
 * the browser's back button behaves, but the shell is the same: one header,
 * one date window, seven views.
 */
export default function ProfitIQ() {
  const businessId = useBusinessId();
  const { isLocked } = useProfitIqAccess();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            // The mobile app revalidates a minute after mount; matching it keeps
            // the two clients from disagreeing about how fresh a figure is.
            staleTime: 60000,
          },
        },
      }),
    []
  );

  if (!businessId) {
    return (
      <Box sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: colors.mutedForeground }}>
          Select a business to see Profit IQ
        </Typography>
        <Typography variant="body2" sx={{ color: colors.faint, mt: 1 }}>
          Profit data is scoped to the active business.
        </Typography>
      </Box>
    );
  }

  // Without access there is no dashboard to date-scope or tab through, so the
  // upgrade prompt stands alone.
  if (isLocked) {
    return <ProfitIqLocked />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ProfitIqProvider>
        {/* The loader the tabs report into covers everything inside here, so no
            control is reachable while a view is loading, and the unsaved-work
            question is asked before any move the section makes. */}
        <ScreenLoadingProvider>
          <UnsavedChangesProvider>
            <ProfitIqSection />
          </UnsavedChangesProvider>
        </ScreenLoadingProvider>
      </ProfitIqProvider>
    </QueryClientProvider>
  );
}

function ProfitIqSection() {
  const history = useHistory();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const { dashboardStale, clearDashboardStale } = useProfitIqContext();
  const guardMove = useUnsavedChangesGuard();
  const hasUnsavedWork = useHasUnsavedWork();
  const pendingRef = useRef(false);

  // Saving a cost or a config no longer refetches the dashboard, so the
  // figures an edit moved are caught up here, at the moment a view that
  // reports them is opened. Read through a ref so opening a tab is what runs
  // this, not the edit that set the flag.
  const staleRef = useRef(dashboardStale);
  useEffect(() => {
    staleRef.current = dashboardStale;
  }, [dashboardStale]);

  const refreshDashboardIfStale = useCallback(() => {
    if (!staleRef.current) return;
    DASHBOARD_KEYS.forEach((key) =>
      queryClient.invalidateQueries({ queryKey: [key], exact: false })
    );
    clearDashboardStale();
  }, [queryClient, clearDashboardStale]);

  // Keyed on the path, so it is opening a view that catches the figures up —
  // not the edit that set the flag, and not a move the guard turned away. Mount
  // counts as an opening too, which covers an edit followed by leaving the
  // section entirely and coming back.
  useEffect(() => {
    refreshDashboardIfStale();
  }, [refreshDashboardIfStale, pathname]);

  // Opening another view leaves whatever the current one has not saved.
  // Tabs are URL-driven here, so this one blocker covers both a tab switch and
  // leaving the section altogether — only the date bar moves without the
  // router, and the header guards those itself. Armed only while there are
  // edits to lose, following the conditional pattern in `src/App.js`, which
  // also keeps this out of the way of that blocker: history holds one prompt
  // at a time.
  useEffect(() => {
    if (!hasUnsavedWork) return undefined;
    const unblock = history.block((nextLocation) => {
      // The answered move comes back through here, since taking it is another
      // navigation; letting it past is what stops the question repeating.
      if (pendingRef.current) return undefined;
      guardMove(() => {
        pendingRef.current = true;
        unblock();
        history.push(`${nextLocation.pathname}${nextLocation.search}`);
        pendingRef.current = false;
      });
      return false;
    });
    return unblock;
  }, [history, guardMove, hasUnsavedWork]);

  // The import wizard is a full-page step-through, not a tab.
  const isImporting = pathname.startsWith(IMPORT_PATH);
  const activeTab =
    TABS.find((tab) => pathname.startsWith(tab.path))?.path ?? TABS[0].path;

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {!isImporting && (
        <>
          <ProfitIqHeader />
          <Tabs
            value={activeTab}
            onChange={(event, next) => history.push(next)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ mb: 2 }}
          >
            {TABS.map((tab) => (
              <Tab
                key={tab.key}
                value={tab.path}
                label={tab.label}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
            ))}
          </Tabs>
        </>
      )}

      <Switch>
        <Route
          exact
          path={BASE_PATH}
          render={() => <Redirect to={TABS[0].path} />}
        />
        <Route exact path={`${BASE_PATH}/overview`} component={Overview} />
        <Route exact path={`${BASE_PATH}/menu-items`} component={MenuItems} />
        <Route exact path={`${BASE_PATH}/categories`} component={Categories} />
        <Route exact path={`${BASE_PATH}/channels`} component={Channels} />
        <Route exact path={`${BASE_PATH}/my-costs`} component={MyCosts} />
        <Route exact path={`${BASE_PATH}/settings`} component={ProfitSettings} />
        <Route exact path={`${BASE_PATH}/insights`} component={Insights} />
        <Route exact path={IMPORT_PATH} component={ImportSales} />
        <Redirect to={TABS[0].path} />
      </Switch>
    </Box>
  );
}
